// Basic
import { useEffect, useState } from 'react';
// Store & actions
import useSidebarActiveButtonStore from '@/app/store/sidebarActiveButton';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useCustomPayStore from '@/app/store/customPay';
import useSessionStore from '@/app/store/session';
import actionsOrganizations from '@/app/modules/organizations/api/actionsOrganizations';
import useTabManager from '../../hooks/useTabManager';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import UsersTable from '@/app/modules/users/monetic/components/tables/UsersTable';
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import RemoteCollectionsTable from '@/app/modules/remoteCollections/components/tables/RemoteCollectionsTable';
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import SimCardsTable from '@/app/modules/sims/components/tables/SimCardsTable';
import ParentPlansTable from '@/app/modules/plans/components/tables/ParentPlansTable';
import ModalAddOrganization from '@/app/modules/organizations/components/modals/addOrganization/ModalAddOrganization';
import ModalAddPlansToOrganization from '@/app/modules/plans/components/modals/addPlansToOrganization/ModalAddPlansToOrganization';
import ModalCreatePlan from '@/app/modules/plans/components/modals/createPlan/ModalCreatePlan';
import ModalSendMailAddUser from '@/app/modules/users/monetic/components/modals/sendMailAddUser/ModalSendMailAddUser';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import Entity from '@/common-utils/components/renders/entity/Entity';
import OrganizationDetailsTab from '@/app/modules/organizations/components/tabs/OrganizationDetailsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
// Common
import RouteUrls from '../../enums/RouteUrls';
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import OrganizationType from '@/app/modules/organizations/enums/OrganizationTypes';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import ICONS from '@/common-utils/enums/icons';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import ORGANIZATION_DETAILS_TABS from '../../enums/tabs/OrganizationDetailsTabs';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsOrganization, nsPlan } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} OrganizationDetails
 */
function OrganizationDetails() {

    // Store
    const isCustomPay = useCustomPayStore(s => s.isCustomPay);
    const { permissions } = useSessionStore(s => s.session);
    const { uuid: loggedUserOrganizationId } = useRootOrganizationStore(s => s.rootOrganization);
    const setActiveButton = useSidebarActiveButtonStore(s => s.setActiveButton);

    // URL Params
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { view, section } = extractQueriesFromUrl(location.search);

    // State
    const [activeTab, setActiveTab] = useState(
        Object.values(ORGANIZATION_DETAILS_TABS).find(tab => tab.name === view)?.name ?? ORGANIZATION_DETAILS_TABS.DEFAULT.name
    );
    const [showModalAddOrganization, setShowModalAddOrganization] = useState(false);
    const [showModalAddPlansToOrganization, setShowModalAddPlansToOrganization] = useState(false);
    const [showModalCreatePlan, setShowModalCreatePlan] = useState(false);
    const [showModalSendMailAddUser, setShowModalSendMailAddUser] = useState(false);

    // API Calls
    const { data: organization, isLoading, isError } = actionsOrganizations.getOrganization(organizationId, true);

    // Hooks
    useEffect(() => {
        if (organizationId === loggedUserOrganizationId) {
            setActiveButton('sb-header-btn');
        }
    }, [organizationId]);

    // Handles tab selection
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: ORGANIZATION_DETAILS_TABS,
        navigate: navigate
    });

    // Constants
    const isOfType = (...organizationTypes) => {
        return organization && organizationTypes.some(organizationType => organization[organizationFields.TYPE.key] === organizationType.name);
    };

    // Redirection
    if (isError) {
        return <Navigate to={RouteUrls.ORGANIZATIONS.url} />;
    }

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.ORGANIZATION_DETAILS}
                    i18nData={{
                        socialReason: organization?.legalName,
                        organizationType: organization?.type
                    }}
                    isLoading={isLoading}
                    routeBase={RouteUrls.ORGANIZATIONS}
                >
                    {
                        isLoading &&
                        <Skeleton className='h-6 w-[300px] p-4 bg-gray-200' />
                    }
                    {
                        !isLoading &&
                        <TabsList>
                            <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.DETAILS} activeTab={activeTab} permissions={permissions} >
                                <Entity entity='page.organization.tabs.details' />
                            </TabsTrigger>
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.USERS} activeTab={activeTab} permissions={permissions} >
                                    <Entity entity='page.organization.tabs.users' />
                                </TabsTrigger>}
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.DIRECT_ORGANIZATIONS} activeTab={activeTab} permissions={permissions} >
                                    <Entity entity='page.organization.tabs.direct_organizations' />
                                </TabsTrigger>}
                            <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.SUBSCRIPTIONS} activeTab={activeTab} permissions={permissions} >
                                <Entity entity='page.organization.tabs.subscriptions' />
                            </TabsTrigger>
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.REMOTE_COLLECTIONS} activeTab={activeTab} permissions={permissions} >
                                    <Entity entity='page.organization.tabs.remote_collections' />
                                </TabsTrigger>}
                            <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.PARENT_PLANS} activeTab={activeTab} permissions={permissions} >
                                <Entity entity='page.organization.tabs.parent_plans' />
                            </TabsTrigger>
                            <TabsTrigger tab={ORGANIZATION_DETAILS_TABS.SIM_CARDS} activeTab={activeTab} permissions={permissions} >
                                <Entity entity='page.organization.tabs.sim_cards' />
                            </TabsTrigger>
                        </TabsList >
                    }
                </Page.Header >
                {
                    isLoading ?
                        <Spinner className={'full-screen-loading'} />
                        :
                        <Page.Body dataCy='page-organization-details'>
                            <TabsContent tab={ORGANIZATION_DETAILS_TABS.DETAILS} permissions={permissions}>
                                <OrganizationDetailsTab organization={organization} section={section} />
                            </TabsContent>
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsContent tab={ORGANIZATION_DETAILS_TABS.USERS} permissions={permissions}>

                                    <UsersTable
                                        filtersEventName={setFiltersEventName('users')}
                                        organizationId={organization.uuid}
                                        pageActions={
                                            <PageActions actions={[{
                                                icon: ICONS.USER_ADD,
                                                entity: 'actions.page.add_user',
                                                namespace: nsOrganization,
                                                onClick: () => setShowModalSendMailAddUser(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [Permissions.USER_CREATE.name]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }]}
                                                retractable
                                            />
                                        } />

                                </TabsContent>}
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsContent tab={ORGANIZATION_DETAILS_TABS.DIRECT_ORGANIZATIONS} permissions={permissions}>

                                    <OrganizationsTable
                                        filtersEventName={setFiltersEventName('organizations')}
                                        organizationId={organizationId}
                                        pageActions={
                                            <PageActions actions={[{
                                                icon: ICONS.ADD,
                                                entity: 'actions.page.add_direct_organization',
                                                namespace: nsOrganization,
                                                onClick: () => setShowModalAddOrganization(true),
                                                visible: !isCustomPay,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.ORGANIZATION_CREATE_CLIENT_ORGANIZATION.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }]}
                                                retractable
                                            />
                                        } />

                                </TabsContent>}
                            <TabsContent tab={ORGANIZATION_DETAILS_TABS.SUBSCRIPTIONS} permissions={permissions}>
                                <SubscriptionsTable
                                    filtersEventName={setFiltersEventName('subscriptions')}
                                    organizationId={organizationId}
                                />
                            </TabsContent>
                            {(isOfType(OrganizationType.WHITE_LABEL)) &&
                                <TabsContent tab={ORGANIZATION_DETAILS_TABS.REMOTE_COLLECTIONS} permissions={permissions}>
                                    <RemoteCollectionsTable
                                        filtersEventName={setFiltersEventName('remote_collections')}
                                        organizationId={organizationId}
                                    />
                                </TabsContent>}
                            <TabsContent tab={ORGANIZATION_DETAILS_TABS.PARENT_PLANS} permissions={permissions}>
                                <ParentPlansTable
                                    filtersEventName={setFiltersEventName('parent_plans')}
                                    organizationId={organizationId}
                                    pageActions={
                                        <PageActions actions={[
                                            {
                                                icon: ICONS.ADD,
                                                entity: 'actions.page.add_existing_plan',
                                                namespace: nsOrganization,
                                                onClick: () => setShowModalAddPlansToOrganization(true),
                                                visible: loggedUserOrganizationId === organization[organizationFields.PROVIDER_UUID.key],
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SUBSCRIPTION_PLAN_ADD_CLIENT.name,
                                                        Permissions.SUBSCRIPTION_PLAN_LIST_CLIENT_PLANS.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                icon: ICONS.PLAN,
                                                entity: 'actions.page.create_plan',
                                                namespace: nsPlan,
                                                onClick: () => setShowModalCreatePlan(true),
                                                visible: loggedUserOrganizationId === organization[organizationFields.PROVIDER_UUID.key],
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [
                                                        Permissions.SUBSCRIPTION_PLAN_CREATE.name,
                                                        Permissions.SUBSCRIPTION_PLAN_LIST_PROVIDER_PLANS.name
                                                    ]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]}
                                            retractable
                                        />
                                    }
                                />
                            </TabsContent>
                            <TabsContent tab={ORGANIZATION_DETAILS_TABS.SIM_CARDS} permissions={permissions}>
                                <SimCardsTable
                                    filtersEventName={setFiltersEventName('sim_cards')}
                                    organizationId={organizationId}
                                />
                            </TabsContent>
                        </Page.Body>
                }
            </Tabs >
            {
                showModalAddOrganization &&
                <ModalAddOrganization
                    isOpen={showModalAddOrganization}
                    setIsOpen={setShowModalAddOrganization}
                    currentOrganizationId={organizationId}
                />
            }
            {
                showModalAddPlansToOrganization &&
                <ModalAddPlansToOrganization
                    isOpen={showModalAddPlansToOrganization}
                    setIsOpen={setShowModalAddPlansToOrganization}
                    currentOrganizationId={organizationId}
                />
            }
            {
                showModalCreatePlan &&
                <ModalCreatePlan
                    isOpen={showModalCreatePlan}
                    setIsOpen={setShowModalCreatePlan}
                />
            }
            {
                showModalSendMailAddUser &&
                <ModalSendMailAddUser
                    isOpen={showModalSendMailAddUser}
                    setIsOpen={setShowModalSendMailAddUser}
                    organizationId={organizationId}
                />
            }
        </Page >
    );
}

export default OrganizationDetails;